import { Badge, Checkbox, Flex, Text } from "@mantine/core";
import type {
  CellContext,
  Column,
  ColumnDef,
  Row,
} from "@tanstack/react-table";
import { createColumnHelper } from "@tanstack/table-core";
import get from "lodash/get";
import type { CSSProperties } from "react";

import { FileDownloadLink } from "@/components/session-results/sections/file-download-link";
import { S3_URI_PREFIX } from "@/constants/s3";
import {
  CompetencyCell,
  ReprocessCell,
  TextCell,
  type TextCellValue,
  UnviewedIndicatorTextCell,
} from "@/components/table/cells";
import ActionMenu from "@/components/table/cells/actionMenu";
import { CreatedAtCell } from "@/components/table/cells/created-at-cell";
import { getCommonPinningStyles } from "@/components/table/utils";
import {
  CandidateSourceBadge,
  CompetencyBadge,
  NotAsked,
  QualifyingBadge,
} from "@/components/upwage-badge";
import {
  BaseTableQueryKeys,
  CreatedAtQueryKeys,
  SortOrder,
  type ApiColumn,
  type BaseTableQueryParams,
  type CreatedAtQueryParams,
  type FilterableCompetencyFits,
} from "@/types/api";
import { QuestionType } from "@/types/screener-builder/questions";
import {
  ClientFitProcessingStatus,
  ClientFitTierLabel,
  Competency,
  CompetencyFit,
  type ClientFitSummary,
  QUALIFYING_PASS_RATE_STRING_PATH,
  Screening,
} from "@/types/screeners";
import type { User } from "@/types/user";
import type { Nullable } from "@/types/utils";
import { numberIsTruthyOrZero } from "@/utils/helpers";
import { formatLanguageLabel } from "@/utils/language-labels";
import { cellWithInfoProp } from "@/utils/table";
import { rowColorCodes } from "@/utils/ui";

export interface ResultsTableQueryParams
  extends BaseTableQueryParams, CreatedAtQueryParams {
  candidateSource: string[];
  completedOnly: boolean;
  highlightColor: string[];
  highTimeSpent: boolean;
  highWordCount: boolean;
  language: string[];
  overallFit: FilterableCompetencyFits;
  unviewed: boolean;
}

export const ResultsQueryKeys = {
  ...BaseTableQueryKeys,
  ...CreatedAtQueryKeys,
  candidateSource: "candidateSource",
  completedOnly: "completedOnly",
  highlightColor: "highlightColor",
  highTimeSpent: "highTimeSpent",
  highWordCount: "highWordCount",
  language: "language",
  overallFit: "overallFit",
  unviewed: "unviewed",
} as const satisfies { [K in keyof ResultsTableQueryParams]: K };

export const ResultsFilterKeys = [
  "candidateSource",
  "completedOnly",
  "createdAtAfter",
  "createdAtBefore",
  "highlightColor",
  "highTimeSpent",
  "highWordCount",
  "language",
  "overallFit",
  "search",
  "unviewed",
] as const satisfies readonly (keyof ResultsTableQueryParams)[];

export enum ResultsTableSortableColumns {
  CandidateName = "candidateName",
  ClientFitTier = "clientFitTier",
  CreatedAt = "createdAt",
  LocationName = "locationName",
  OverallFit = "overallFit",
  QualifyingPassRate = "qualifyingPassRate",
  TotalTimeInSeconds = "totalTimeInSeconds",
  TotalWords = "totalWords",
}

export const RESULTS_DEFAULT_SORT = {
  sortBy: ResultsTableSortableColumns.CreatedAt,
  sortOrder: SortOrder.Desc,
} as const;

export const getJobPinningStyles = (
  column: Column<Screening>,
  row?: Row<Screening>,
): CSSProperties => ({
  ...getCommonPinningStyles(column, row),
  backgroundColor: row?.original.highlightColor
    ? rowColorCodes[row.original.highlightColor]
    : "white",
});

const CLIENT_FIT_TIER_BADGE_STYLES: Record<
  ClientFitTierLabel,
  { background: string; text: string }
> = {
  [ClientFitTierLabel.StrongMatch]: {
    background: "#B8ECE3",
    text: "#005648",
  },
  [ClientFitTierLabel.PotentialMatch]: {
    background: "#FFF4B2",
    text: "#706000",
  },
  [ClientFitTierLabel.Review]: {
    background: "#DDDDDD",
    text: "#474747",
  },
};

const CLIENT_FIT_STATUS_LABELS: Record<ClientFitProcessingStatus, string> = {
  [ClientFitProcessingStatus.New]: "Queued",
  [ClientFitProcessingStatus.InProgress]: "Processing",
  [ClientFitProcessingStatus.Complete]: "Complete",
  [ClientFitProcessingStatus.Error]: "Error",
  [ClientFitProcessingStatus.Stale]: "Reprocessing",
  [ClientFitProcessingStatus.Skipped]: "Skipped",
};

const ClientFitCell = ({ clientFit }: { clientFit?: ClientFitSummary }) => {
  if (!clientFit) {
    return null;
  }

  if (
    clientFit.processingStatus !== ClientFitProcessingStatus.Complete ||
    !clientFit.tierLabel
  ) {
    return (
      <Text c="gray.6" size="sm" className="whitespace-nowrap">
        {clientFit.processingStatus
          ? CLIENT_FIT_STATUS_LABELS[clientFit.processingStatus]
          : "Not processed"}
      </Text>
    );
  }

  const colors = CLIENT_FIT_TIER_BADGE_STYLES[clientFit.tierLabel];

  return (
    <Flex align="center" gap={8} wrap="nowrap">
      <Badge
        c={colors.text}
        className="px-3 py-0.5 text-xs whitespace-nowrap"
        color={colors.background}
        size="lg"
        variant="filled"
      >
        {clientFit.tierLabel}
      </Badge>
      <Text c="gray.7" size="xs" className="whitespace-nowrap">
        {clientFit.greenFlagCount ?? 0} green / {clientFit.yellowFlagCount ?? 0}{" "}
        yellow
      </Text>
    </Flex>
  );
};

export const formatJobColumns = (
  columns: ApiColumn<Screening>[],
  user: User | null,
  showCandidateSource = false,
  // See explanation at
  // components/table/views/screeners/screeners-columns.ts:formatScreenerColumns
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): ColumnDef<Screening, any>[] => {
  // Filter out candidateSource column when feature flag is off
  const filteredColumns = showCandidateSource
    ? columns
    : columns.filter((col) => col.fieldName !== "candidateSource");
  const columnHelper = createColumnHelper<Screening>();

  const formattedColumns = filteredColumns.map((column) => {
    const { fieldName, internalName: header } = column;

    if (fieldName.startsWith("competencies")) {
      const competencyName = fieldName.slice(fieldName.indexOf(".") + 1);

      return columnHelper.accessor(
        (originalRow) => get(originalRow, ["competencies", competencyName]),
        {
          cell: cellWithInfoProp<Screening, Competency>(CompetencyCell, {}),
          header,
          // Use fieldName as ID to ensure uniqueness when same name exists in showInSummary
          id: fieldName,
        },
      );
    }

    if (fieldName.startsWith("showInSummary")) {
      const propertyName = fieldName.slice(fieldName.indexOf(".") + 1);

      return columnHelper.accessor(
        (originalRow) => get(originalRow, ["showInSummary", propertyName]),
        {
          cell: (info) => {
            if (info.getValue() === CompetencyFit.NotAsked) {
              return <NotAsked />;
            }

            const meta = info.row.original.showInSummaryMeta?.[propertyName];

            if (
              meta?.inputType === QuestionType.FileUpload &&
              meta.questionRootId &&
              String(info.getValue()).startsWith(S3_URI_PREFIX)
            ) {
              return (
                <FileDownloadLink
                  candidateName={info.row.original.candidateName}
                  createdAt={info.row.original.createdAt}
                  internalLabel={propertyName}
                  jobTitle={info.row.original.jobTitle}
                  questionRootId={meta.questionRootId}
                  s3Uri={String(info.getValue())}
                  sessionId={info.row.original.sessionId}
                />
              );
            }

            return <TextCell info={info} />;
          },
          header,
          // Use fieldName as ID to ensure uniqueness when same name exists in competencies
          id: fieldName,
        },
      );
    }

    switch (fieldName) {
      case "clientFit.tierLabel":
        return columnHelper.accessor((row) => row.clientFit?.tierLabel, {
          cell: (info) => (
            <ClientFitCell clientFit={info.row.original.clientFit} />
          ),
          header,
          id: ResultsTableSortableColumns.ClientFitTier,
        });
      case "candidateName":
        return columnHelper.accessor(fieldName, {
          cell: cellWithInfoProp<Screening, Nullable<string>>(
            UnviewedIndicatorTextCell,
            {},
          ),
          header,
        });
      case "createdAt":
        return columnHelper.accessor(fieldName, {
          cell: cellWithInfoProp<Screening, string>(CreatedAtCell, {}),
          header,
        });
      case QUALIFYING_PASS_RATE_STRING_PATH:
        return columnHelper.accessor(
          (row) => row.qualifyingSummary?.qualifyingPassRate ?? undefined,
          {
            cell: ({ row: { original } }) => {
              if (!original.qualifyingSummary) {
                return null;
              }
              const rawScore =
                numberIsTruthyOrZero(original.rawQualifyingQuestionsTotal) &&
                numberIsTruthyOrZero(original.rawQualifyingQuestionsQualified)
                  ? `${original.rawQualifyingQuestionsQualified}/${original.rawQualifyingQuestionsTotal}`
                  : undefined;

              return (
                <QualifyingBadge
                  rawScore={rawScore}
                  score={original.qualifyingSummary.qualifyingPassRate}
                />
              );
            },
            header,
            id: ResultsTableSortableColumns.QualifyingPassRate,
          },
        );
      case "overallFit":
        return columnHelper.accessor(fieldName, {
          cell: (info) => {
            const { overallFit, rawOverallFitScore } = info.row.original;

            if (!overallFit) {
              return null;
            }

            return (
              <CompetencyBadge
                isOverallFit
                rawScore={rawOverallFitScore}
                score={overallFit}
              />
            );
          },
          header,
        });
      case "words":
      case "time":
        return columnHelper.accessor(fieldName, {
          cell: cellWithInfoProp<Screening, TextCellValue>(TextCell, {}),
          header,
          id:
            fieldName === "words"
              ? ResultsTableSortableColumns.TotalWords
              : ResultsTableSortableColumns.TotalTimeInSeconds,
        });
      case "candidateSource":
        return columnHelper.accessor(fieldName, {
          cell: (info) => {
            const value = info.getValue();
            if (!value) return null;
            return <CandidateSourceBadge source={value} />;
          },
          header,
          enableSorting: false,
        });
      case "language":
        // AS-1834: TalentHub stores raw locale codes (`es-MX`, `mx-MX`,
        // `en-US`, etc.) in `screener_session.language`. The default
        // TextCell renderer would show those codes verbatim — instead map
        // through `formatLanguageLabel` so recruiters see readable labels
        // ("Spanish (Mexican)"). The accessor wraps the value so TextCell's
        // existing truncate/tooltip behavior is preserved.
        return columnHelper.accessor(
          (row) => (row.language ? formatLanguageLabel(row.language) : null),
          {
            cell: cellWithInfoProp<Screening, TextCellValue>(TextCell, {}),
            header,
            id: "language",
            enableSorting: false,
          },
        );
      default:
        return columnHelper.accessor(fieldName, {
          cell: cellWithInfoProp<Screening, TextCellValue>(TextCell, {}),
          header,
        });
    }
  });

  const actionsColumn = {
    header: "Actions",
    accessorKey: "actions",
    cell: (info: CellContext<Screening, void>) => (
      <div
        className="flex items-center gap-2"
        onClick={(e) => e.stopPropagation()}
      >
        <ActionMenu row={info.row.original} />
        {user?.isInternal && <ReprocessCell info={info} />}
      </div>
    ),
  };

  const highlightColorColumn = {
    header: "",
    accessorKey: "highlightColor",
    cell: () => null,
    isVisible: false,
  };

  const selectionColumn: ColumnDef<Screening> = {
    id: "selection",
    header: ({ table }) => (
      <Checkbox
        checked={table.getIsAllPageRowsSelected()}
        onChange={table.getToggleAllPageRowsSelectedHandler()}
        indeterminate={table.getIsSomePageRowsSelected()}
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onChange={row.getToggleSelectedHandler()}
        onClick={(event) => {
          event.stopPropagation();
        }}
      />
    ),
  };

  return [
    selectionColumn,
    ...formattedColumns,
    actionsColumn,
    highlightColorColumn,
  ];
};
